import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { NodeSelection } from '@tiptap/pm/state';

/**
 * ImageSelect
 * -----------
 * Defensive selection + deletion behavior for block-level images.
 *
 * Why this exists:
 *  - When a user pastes an image from the clipboard (Snipping Tool /
 *    screenshot), ProseMirror should auto-create a NodeSelection on
 *    click and Backspace/Delete should remove the image. In practice
 *    that flow is fragile — clicks sometimes land on an interposed
 *    wrapper or paragraph caret, leaving the image *not* selected.
 *    The user then presses Backspace expecting the image to disappear
 *    but instead the previous character is deleted.
 *
 *  - This plugin forces a NodeSelection whenever the user mouses down
 *    on an `<img>` inside the editor, and provides Backspace/Delete
 *    fallbacks that remove an image when the cursor is parked right
 *    before/after it (typical post-paste state).
 */
export const ImageSelect = Extension.create({
  name: 'imageSelect',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            mousedown(view, event) {
              const target = event.target as HTMLElement | null;
              if (!target) return false;
              const img = target.closest('img');
              if (!img || !view.dom.contains(img)) return false;
              const pos = view.posAtDOM(img, 0);
              if (pos < 0) return false;
              const resolved = view.state.doc.resolve(pos);
              // Walk up to find the image node position (image is usually
              // a leaf block; the resolved depth points just inside it).
              let nodePos = pos;
              for (let d = resolved.depth; d >= 0; d--) {
                const node = resolved.node(d);
                if (node && node.type.name === 'image') {
                  nodePos = resolved.before(d);
                  break;
                }
              }
              // Fallback: try the position before the resolved depth.
              const docNode = view.state.doc;
              try {
                const nodeAtPos = docNode.nodeAt(nodePos);
                if (!nodeAtPos || nodeAtPos.type.name !== 'image') {
                  // Try the immediate "before" position if the image is a
                  // top-level block (common with our config: inline=false).
                  const before = Math.max(0, pos - 1);
                  const candidate = docNode.nodeAt(before);
                  if (candidate && candidate.type.name === 'image') {
                    nodePos = before;
                  } else {
                    return false;
                  }
                }
              } catch {
                return false;
              }
              const tr = view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodePos));
              view.dispatch(tr);
              view.focus();
              event.preventDefault();
              return true;
            },
          },
        },
      }),
    ];
  },

  addKeyboardShortcuts() {
    const tryDelete = (direction: 'before' | 'after') => () => {
      const { editor } = this;
      if (!editor) return false;
      const { state } = editor;
      const { selection } = state;
      // If the user already has the image as a NodeSelection, let
      // ProseMirror's default deletion handle it (cleaner).
      if ((selection as { node?: unknown }).node) {
        return false;
      }
      if (!selection.empty) return false;
      const { $from } = selection;
      // Look at the node immediately before (Backspace) or after (Delete)
      // the cursor and delete it if it's an image.
      if (direction === 'before') {
        const before = $from.nodeBefore;
        if (before && before.type.name === 'image') {
          const start = $from.pos - before.nodeSize;
          editor.chain().focus().command(({ tr, dispatch }) => {
            if (dispatch) dispatch(tr.delete(start, $from.pos));
            return true;
          }).run();
          return true;
        }
        // Also handle the case where the cursor is at the start of an
        // empty paragraph that follows an image — Backspace then deletes
        // the paragraph and lands the user on/after the image.
        if (!before && $from.parent.content.size === 0) {
          // Find previous sibling at the parent's parent.
          const parentPos = $from.before($from.depth);
          if (parentPos > 0) {
            const prev = state.doc.resolve(parentPos).nodeBefore;
            if (prev && prev.type.name === 'image') {
              // Delete the empty paragraph so the image is now adjacent
              // to whatever was above the paragraph.
              editor.chain().focus().command(({ tr, dispatch }) => {
                if (dispatch) dispatch(tr.delete(parentPos, parentPos + $from.parent.nodeSize));
                return true;
              }).run();
              return true;
            }
          }
        }
      } else {
        const after = $from.nodeAfter;
        if (after && after.type.name === 'image') {
          const end = $from.pos + after.nodeSize;
          editor.chain().focus().command(({ tr, dispatch }) => {
            if (dispatch) dispatch(tr.delete($from.pos, end));
            return true;
          }).run();
          return true;
        }
      }
      return false;
    };

    return {
      Backspace: tryDelete('before'),
      Delete: tryDelete('after'),
    };
  },
});
